/**
 * Settings/Profile Data Phase 2 — admin widgets.
 *
 * Three pieces:
 *   • <ProfileDataIntelligenceBanner /> — header banner with
 *     KPIs (total / complete / incomplete / no segmentation).
 *   • <ProfileDataCompletenessCell user_id /> — inline column
 *     content for the AdminUsers row.
 *   • <InternalControlFieldsDrawer userId onClose /> — overlay
 *     drawer for editing L4 admin-only fields.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { X, ShieldCheck, FlaskConical, History } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { extractErrorMessage } from "@/lib/api";

const LIFECYCLE = [
  "prospect", "lead", "qualified", "active",
  "at_risk", "dormant", "churned",
];
const SEGMENTATION = [
  "ambassador", "key_account", "early_adopter",
  "tester", "vip", "standard",
];
const PRIORITY = ["low", "normal", "high", "urgent"];

const _label = (t, key) => t(`admin.profileDataP2.enum.${key}`) || key;

// ---------------------------------------------------------------------------
// Banner
// ---------------------------------------------------------------------------

export const ProfileDataIntelligenceBanner = ({ snapshot }) => {
  const { t } = useLang();
  if (!snapshot) return null;
  const b = snapshot.banner || {};
  const cards = [
    { key: "total", value: b.total, tone: "slate" },
    { key: "complete", value: b.complete, tone: "emerald" },
    { key: "incomplete", value: b.incomplete, tone: "amber" },
    { key: "empty", value: b.empty, tone: "rose" },
    { key: "no_segmentation", value: b.no_segmentation, tone: "purple" },
  ];
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-5 gap-3"
      data-testid="profile-data-intelligence-banner"
    >
      {cards.map(({ key, value, tone }) => (
        <div
          key={key}
          className={`rounded-xl border bg-white p-3 border-${tone}-200`}
          data-testid={`profile-data-intel-card-${key}`}
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            {t(`admin.profileDataP2.banner.${key}`)}
          </div>
          <div className={`mt-1 text-xl font-bold text-${tone}-700`}>
            {value ?? "—"}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Completeness cell
// ---------------------------------------------------------------------------

export const ProfileDataCompletenessCell = ({ summary, onOpen }) => {
  const { t } = useLang();
  if (!summary) return <span className="text-slate-300 text-xs">—</span>;
  const pct = summary.completion_percent ?? 0;
  const tone = pct >= 80 ? "emerald" : pct === 0 ? "slate" : "amber";
  const lifecycleLabel = summary.lifecycle_stage
    ? _label(t, summary.lifecycle_stage)
    : null;
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 text-xs font-medium text-${tone}-700 hover:underline`}
      onClick={onOpen}
      data-testid={`profile-data-completeness-${summary.user_id}`}
      title={summary.last_edit || ""}
    >
      <span className={`inline-block w-2 h-2 rounded-full bg-${tone}-500`} />
      {pct}%
      {lifecycleLabel && (
        <span className="text-[10px] text-slate-500 tracking-wider">
          · {lifecycleLabel}
        </span>
      )}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Drawer — L4 internal_control_fields editor + audit log
// ---------------------------------------------------------------------------

export const InternalControlFieldsDrawer = ({ userId, onClose, onSaved }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [fields, setFields] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [completion, setCompletion] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [icf, comp, audit] = await Promise.all([
        api.get(`/admin/users/${userId}/internal-control-fields`),
        api.get(`/admin/users/${userId}/completion`),
        api.get(`/admin/users/${userId}/audit-log?limit=20`),
      ]);
      setUser(icf.data.user);
      setFields(icf.data.internal_control_fields || {});
      setCompletion(comp.data);
      setAuditLog(audit.data.items || []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const tagText = useMemo(
    () => (Array.isArray(fields.tags) ? fields.tags.join(", ") : ""),
    [fields.tags]
  );
  const setTagText = (s) => {
    const arr = s.split(",").map((x) => x.trim()).filter(Boolean);
    setField("tags", arr);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        notes: fields.notes || null,
        tags: fields.tags || [],
        segmentation_admin: fields.segmentation_admin || null,
        lifecycle_stage: fields.lifecycle_stage || null,
        operational_priority: fields.operational_priority || null,
      };
      const { data } = await api.put(
        `/admin/users/${userId}/internal-control-fields`, payload,
      );
      toast.success(
        data.changed_fields?.length
          ? t("admin.profileDataP2.savedWithChanges").replace("{n}", data.changed_fields.length)
          : t("admin.profileDataP2.noChanges"),
      );
      if (onSaved) onSaved();
      // Reload to refresh audit log with the new rows.
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="internal-control-fields-drawer-overlay"
    >
      <div
        className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="internal-control-fields-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between z-10 rounded-t-2xl">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-purple-700 font-semibold flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              {t("admin.profileDataP2.drawerTitle")}
            </div>
            <h2 className="font-display font-semibold text-lg text-slate-900 mt-0.5">
              {user?.full_name || user?.email || userId}
            </h2>
            {completion && (
              <p className="text-xs text-slate-500 mt-1">
                {t("admin.profileDataP2.completionLabel")}: {" "}
                <span className="font-semibold text-slate-700">
                  {completion.percentage ?? completion.overall_percent ?? 0}%
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-700"
            onClick={onClose}
            data-testid="internal-control-fields-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-slate-500 text-sm">{t("common.loading")}</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Row 1: 3 enum selects in a responsive grid — they breathe
                better side-by-side on desktop, stack on mobile. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                  {t("admin.profileDataP2.fields.lifecycleStage")}
                </label>
                <select
                  className="tsw-input mt-1"
                  value={fields.lifecycle_stage || ""}
                  onChange={(e) => setField("lifecycle_stage", e.target.value || null)}
                  data-testid="icf-lifecycle-stage"
                >
                  <option value="">{t("admin.profileDataP2.enum.unset")}</option>
                  {LIFECYCLE.map((s) => <option key={s} value={s}>{_label(t, s)}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                  {t("admin.profileDataP2.fields.segmentationAdmin")}
                </label>
                <select
                  className="tsw-input mt-1"
                  value={fields.segmentation_admin || ""}
                  onChange={(e) => setField("segmentation_admin", e.target.value || null)}
                  data-testid="icf-segmentation-admin"
                >
                  <option value="">{t("admin.profileDataP2.enum.unset")}</option>
                  {SEGMENTATION.map((s) => <option key={s} value={s}>{_label(t, s)}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                  {t("admin.profileDataP2.fields.operationalPriority")}
                </label>
                <select
                  className="tsw-input mt-1"
                  value={fields.operational_priority || ""}
                  onChange={(e) => setField("operational_priority", e.target.value || null)}
                  data-testid="icf-operational-priority"
                >
                  <option value="">{t("admin.profileDataP2.enum.unset")}</option>
                  {PRIORITY.map((s) => <option key={s} value={s}>{_label(t, s)}</option>)}
                </select>
              </div>
            </div>

            {/* Row 2: Tags — full-width, less visual weight than Notes
                but still gets the full row so chip-style content fits. */}
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                {t("admin.profileDataP2.fields.tags")}
              </label>
              <input
                className="tsw-input mt-1"
                value={tagText}
                placeholder={t("admin.profileDataP2.fields.tagsPlaceholder")}
                onChange={(e) => setTagText(e.target.value)}
                data-testid="icf-tags"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {t("admin.profileDataP2.fields.tagsHint")}
              </p>
            </div>

            {/* Row 3: Notes — visually prioritised. Generous min-height
                + vertical resize handle so admins can grow it to fit
                a long CRM note without scroll-claustrophobia. */}
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wider flex items-center justify-between">
                <span>{t("admin.profileDataP2.fields.notes")}</span>
                <span className="normal-case tracking-normal text-[10px] text-slate-400">
                  {(fields.notes || "").length} / 2000
                </span>
              </label>
              <textarea
                className="tsw-input mt-1 leading-relaxed resize-y"
                rows={10}
                maxLength={2000}
                style={{ minHeight: "260px" }}
                value={fields.notes || ""}
                onChange={(e) => setField("notes", e.target.value)}
                data-testid="icf-notes"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {t("admin.profileDataP2.fields.notesHint")}
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <button
                type="button"
                className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
                onClick={() => setAuditOpen((x) => !x)}
                data-testid="icf-audit-toggle"
              >
                <History className="w-3.5 h-3.5" />
                {auditOpen
                  ? t("admin.profileDataP2.hideAudit")
                  : t("admin.profileDataP2.showAudit").replace("{n}", auditLog.length)}
              </button>
              <button
                type="button"
                className="tsw-btn-primary"
                onClick={save}
                disabled={saving}
                data-testid="icf-save"
              >
                <FlaskConical className="w-4 h-4 me-1.5" />
                {saving ? t("common.saving") : t("common.save")}
              </button>
            </div>

            {auditOpen && (
              <div
                className="rounded-lg bg-slate-50 border border-slate-200 divide-y divide-slate-200 text-xs"
                data-testid="icf-audit-list"
              >
                {auditLog.length === 0 ? (
                  <div className="p-3 text-slate-400">
                    {t("admin.profileDataP2.audit.empty")}
                  </div>
                ) : (
                  auditLog.map((row) => (
                    <div key={row.id} className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700">
                          {row.field_path.replace("internal_control_fields.", "")}
                        </span>
                        <span className="text-slate-500">
                          {row.ts && new Date(row.ts).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-slate-600 mt-1">
                        <span className="line-through text-slate-400">
                          {row.old_value_redacted == null ? "—" : String(row.old_value_redacted)}
                        </span>{" "}
                        →{" "}
                        <span className="text-slate-800">
                          {row.new_value_redacted == null ? "—" : String(row.new_value_redacted)}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {row.actor_role} · {row.actor_id}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
